/**
 * The masking rules of session replay (usage/replay.ts) and their check.
 * Pure, no imports: used by the recorder settings (`maskReplayAttribute`)
 * and, as a second line, by the `before_send` filter (`isMaskedRecording`,
 * usage/scrub.ts), which drops a replay chunk unless every text and
 * attribute value in it is masked, whatever the recorder did.
 */

/** Attributes the replay needs for the layout; every other attribute value is masked. */
const LAYOUT_ATTRIBUTES: ReadonlySet<string> = new Set([
  'class',
  'style',
  'type',
  'role',
  'rel',
  'media',
  'dir',
  'lang',
  'width',
  'height',
  'colspan',
  'rowspan',
  'tabindex',
  'disabled',
  'readonly',
  'checked',
  'selected',
  'hidden',
  'open',
  'aria-hidden',
  'aria-expanded',
  'aria-selected',
  'aria-checked',
  'aria-disabled',
  'aria-pressed',
  'aria-current',
  // rrweb's own: the inlined stylesheet
  '_cssText',
]);

/** rrweb's own layout attributes (`rr_width`, `rr_top`, ... of a blocked element); not its image data. */
const RRWEB_LAYOUT = /^rr_(?!dataURL$)[A-Za-z]+$/;

export const MASKED_ATTRIBUTE = '***';

/** An address inside an inline style. */
const STYLE_URL = /url\(\s*[^)\s]/i;

/** `maskAttributeFn`: layout attributes stay (inline-style URLs removed), all others are masked. */
export const maskReplayAttribute = (name: string, value: string, element?: Element): string => {
  // a stylesheet's address (a static file of the app), kept so the replay can style the page
  if (name === 'href' && element?.tagName === 'LINK') return value;
  if (!LAYOUT_ATTRIBUTES.has(name) && !RRWEB_LAYOUT.test(name)) return MASKED_ATTRIBUTE;
  return name === 'style' ? value.replace(/url\([^)]*\)/gi, 'url()') : value;
};

// ---- the check ------------------------------------------------------------------------

/** Masked text: rrweb's `*` per character, white space, nothing else. */
const MASKED_TEXT = /^[\s*]*$/;

/** rrweb's stand-in for the content of a script element. */
const SCRIPT_PLACEHOLDER = 'SCRIPT_PLACEHOLDER';

// rrweb event, node and incremental-source numbers (@rrweb/types)
const EVENT = { fullSnapshot: 2, incremental: 3, meta: 4, custom: 5, plugin: 6 } as const;
const NODE = { element: 2, text: 3, cdata: 4, comment: 5 } as const;
const SOURCE = { mutation: 0, input: 5 } as const;

type Json = Record<string, unknown>;
const asJson = (value: unknown): Json =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : {};
const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** A page address in the replay, as the route-pattern filter would send it. */
export type IsMaskedUrl = (href: string) => boolean;

const maskedText = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && MASKED_TEXT.test(value));

const maskedAttribute = (name: string, value: unknown, tagName: string): boolean => {
  if (typeof value !== 'string') return value === null || typeof value !== 'object';
  // empty: nothing to read (the recorder does not pass empty values through the mask)
  if (value === '') return true;
  if (name === 'style' && STYLE_URL.test(value)) return false;
  // what the recorder had to make of it: the same value for a layout attribute, *** otherwise
  return maskReplayAttribute(name, value, { tagName } as Element) === value;
};

const maskedAttributes = (attributes: unknown, tagName: string): boolean =>
  Object.entries(asJson(attributes)).every(([name, value]) => {
    // a style change arrives as { property: value | [value, priority] | false }
    if (name === 'style' && value && typeof value === 'object') {
      return !STYLE_URL.test(JSON.stringify(value));
    }
    return maskedAttribute(name, value, tagName);
  });

/** A serialized DOM node and everything below it. */
const maskedNode = (node: unknown, parentTag = '', depth = 0): boolean => {
  const n = asJson(node);
  if (depth > 1000) return false;
  switch (n.type) {
    case NODE.text:
      // the text of a style element is the stylesheet, of a script element rrweb's placeholder
      if (n.isStyle === true || parentTag === 'style') return true;
      if (parentTag === 'script')
        return n.textContent === SCRIPT_PLACEHOLDER || maskedText(n.textContent);
      return maskedText(n.textContent);
    case NODE.cdata:
    case NODE.comment:
      return maskedText(n.textContent);
    case NODE.element: {
      const tagName = String(n.tagName ?? '').toLowerCase();
      return (
        maskedAttributes(n.attributes, tagName.toUpperCase()) &&
        asList(n.childNodes).every((child) => maskedNode(child, tagName, depth + 1))
      );
    }
    default:
      return asList(n.childNodes).every((child) => maskedNode(child, parentTag, depth + 1));
  }
};

/** Every `href` anywhere in a custom event's payload is a filtered page address. */
const maskedHrefs = (value: unknown, isMaskedUrl: IsMaskedUrl, depth = 0): boolean => {
  if (!value || typeof value !== 'object' || depth > 8) return true;
  return Object.entries(value as Json).every(([key, entry]) =>
    key === 'href'
      ? typeof entry === 'string' && isMaskedUrl(entry)
      : maskedHrefs(entry, isMaskedUrl, depth + 1),
  );
};

const maskedIncremental = (data: Json): boolean => {
  switch (data.source) {
    case SOURCE.mutation:
      return (
        asList(data.texts).every((text) => maskedText(asJson(text).value)) &&
        // the element of a changed attribute is not in the event; judged without its tag
        asList(data.attributes).every((change) =>
          maskedAttributes(asJson(change).attributes, ''),
        ) &&
        asList(data.adds).every((add) => maskedNode(asJson(add).node))
      );
    case SOURCE.input:
      return maskedText(data.text);
    default:
      // pointer, scroll, viewport, stylesheet rules, media, selection ...: no page text
      return true;
  }
};

/**
 * True when every event of a replay chunk is masked: all text `*`, every
 * attribute a layout value or `***`, no address in inline styles, page
 * addresses filtered, no plugin data (network, console). Anything it can not
 * read (a compressed chunk, an unknown event) counts as not masked.
 */
export const isMaskedRecording = (data: unknown, isMaskedUrl: IsMaskedUrl): boolean => {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.every((item) => {
    const event = asJson(item);
    const payload = asJson(event.data);
    switch (event.type) {
      case EVENT.fullSnapshot:
        return maskedNode(payload.node);
      case EVENT.incremental:
        return maskedIncremental(payload);
      case EVENT.meta:
        return typeof payload.href === 'string' && isMaskedUrl(payload.href);
      case EVENT.custom:
        return maskedHrefs(payload.payload, isMaskedUrl);
      case EVENT.plugin:
        return false;
      default:
        return typeof event.type === 'number' && event.type >= 0 && event.type <= 1;
    }
  });
};
